import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, type ContextInfo } from '../api';

/**
 * §16 org/client switcher (Access Portal style). Changes the ACTIVE account context: the api re-issues the session for
 * that tenant and every query then runs under that tenant's row-level security — one client's world at a time.
 */
export function ContextSwitcher({
  contexts,
  activeTenantId,
  activeName,
}: {
  contexts: ContextInfo[];
  activeTenantId: string;
  /** §17 the active tenant's brand display name, if it set one. */
  activeName?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const nav = useNavigate();
  const active = contexts.find((c) => c.tenantId === activeTenantId);
  if (contexts.length <= 1) {
    return (
      <span className="context-single" title={t('context.single')}>
        {active?.tenantType === 'personal' ? t('context.personal') : activeName || active?.tenantName}
      </span>
    );
  }
  return (
    <select
      className="context-switch"
      aria-label={t('context.switch')}
      value={activeTenantId}
      onChange={async (e) => {
        await api.post('/auth/switch', { tenantId: e.target.value });
        qc.clear(); // nothing from the previous client survives in the cache
        nav('/', { replace: true });
      }}
    >
      {contexts.map((c) => (
        <option key={c.tenantId} value={c.tenantId}>
          {c.tenantType === 'personal'
            ? t('context.personal')
            : (c.tenantId === activeTenantId && activeName) || c.tenantName}
          {c.organisation ? ` — ${t('context.as', { org: c.organisation })}` : ''}
        </option>
      ))}
    </select>
  );
}
